import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { api } from "@/lib/api";
import { StarRating } from "@/components/site/StarRating";

export function ReviewCard({ r, index = 0, showTrip = false }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (index % 3) * 0.08 }}
      className="relative rounded-2xl bg-card border border-border p-6 flex flex-col" data-testid="review-card">
      <Quote className="absolute top-5 right-5 h-8 w-8 text-gold/30" />
      <StarRating value={r.rating} />
      <p className="mt-3 text-foreground/85 leading-relaxed flex-1">“{r.text}”</p>
      <div className="mt-5 flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center font-heading text-lg">{r.name?.[0]}</div>
        <div className="min-w-0">
          <p className="font-medium leading-tight" data-testid="review-name">{r.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {[r.city, showTrip ? r.trip_name : r.travelled_on].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function TripReviews({ trip }) {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get(`/reviews?trip_id=${trip.id}`).then(({ data }) => setReviews(data));
  }, [trip.id]);

  if (!reviews.length) return null;
  const dist = [5, 4, 3, 2, 1].map((n) => ({ n, c: reviews.filter((r) => r.rating === n).length }));

  return (
    <div data-testid="trip-reviews">
      <h2 className="font-heading text-3xl mb-4">Traveller Reviews</h2>
      <div className="rounded-2xl bg-muted/60 p-6 grid sm:grid-cols-[auto_1fr] gap-6 items-center mb-6">
        <div className="text-center sm:pr-6 sm:border-r border-border">
          <p className="font-heading text-6xl text-primary leading-none" data-testid="avg-rating">{trip.avg_rating}</p>
          <StarRating value={trip.avg_rating} className="mt-2 justify-center" />
          <p className="text-xs text-muted-foreground mt-1">{trip.review_count} review{trip.review_count === 1 ? "" : "s"}</p>
        </div>
        <div className="space-y-1.5">
          {dist.map(({ n, c }) => (
            <div key={n} className="flex items-center gap-3 text-xs">
              <span className="w-6 text-muted-foreground">{n}★</span>
              <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
                <div className="h-full bg-gold rounded-full transition-all duration-700" style={{ width: `${(c / reviews.length) * 100}%` }} />
              </div>
              <span className="w-6 text-right text-muted-foreground">{c}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {reviews.map((r, i) => <ReviewCard key={r.id} r={r} index={i} />)}
      </div>
    </div>
  );
}
